import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './index.css';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient.js';
import { initErrorTracking } from './lib/errorTracking.js';
import { initSEOOptimizations } from './utils/seoEnhancements.js';
import { initBatteryMonitoring } from './hooks/useQualitySettings.js';
import './utils/stripeDebug.js'; // Debug Stripe configuration
import { initGoogleAnalytics } from './lib/tracking.js';

const runDeferredBootstrap = () => {
  const start = () => {
    initSEOOptimizations();
    initBatteryMonitoring(); // For intelligent pixel ratio detection
    initGoogleAnalytics();
    void initErrorTracking();
  };

  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(start, { timeout: 1500 });
  } else {
    window.setTimeout(start, 200);
  }
};

runDeferredBootstrap();

function ErrorFallback({ error, componentStack }) {
  const [ErrorProgress, setErrorProgress] = useState(null);

  useEffect(() => {
    let mounted = true;
    import('./components/ErrorReportingProgress.jsx')
      .then((mod) => {
        if (mounted) setErrorProgress(() => mod.default);
      })
      .catch(() => {
        // Pas critique pour l'affichage d'erreurs; évite de bloquer le rendu.
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 flex flex-col items-center justify-center px-6 text-center relative overflow-hidden">
      {/* Animated background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30 dark:opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-amber-200 dark:bg-amber-900/20 blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-rose-200 dark:bg-rose-900/20 blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-orange-200 dark:bg-orange-900/20 blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-lg space-y-6 relative z-10">
        {/* Artistic lampshade illustration */}
        <div className="flex justify-center mb-4">
          <div className="relative">
            {/* Decorative lampshade icon */}
            <svg className="w-32 h-32 text-amber-400 dark:text-amber-500 drop-shadow-lg animate-bounce" style={{ animationDuration: '3s' }} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Lampshade cone */}
              <path d="M30 30 L50 10 L70 30 L65 60 L35 60 Z" fill="currentColor" opacity="0.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              {/* Inner glow */}
              <ellipse cx="50" cy="27" rx="15" ry="8" fill="#FFD700" opacity="0.4"/>
              {/* Light rays */}
              <line x1="50" y1="15" x2="50" y2="5" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
              <line x1="38" y1="20" x2="30" y2="12" stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
              <line x1="62" y1="20" x2="70" y2="12" stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
              {/* Decorative bottom edge */}
              <path d="M35 60 Q40 58 50 58 T65 60" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.8"/>
              {/* Hanging cord */}
              <line x1="50" y1="60" x2="50" y2="75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <circle cx="50" cy="78" r="3" fill="currentColor" opacity="0.6"/>
            </svg>
            {/* Animated sparkles */}
            <div className="absolute -top-2 -right-2 text-amber-400 animate-ping">✨</div>
            <div className="absolute -bottom-2 -left-2 text-rose-400 animate-ping" style={{ animationDelay: '1s' }}>✨</div>
          </div>
        </div>

        {/* Artisanal message */}
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-700 dark:text-amber-400 font-semibold">Un petit accroc dans le tissu...</p>
          <h1 className="text-3xl md:text-4xl font-display text-amber-900 dark:text-[#F5DEB3] leading-tight">
            Aïe, un fil s'est emmêlé !
          </h1>
          <div className="max-w-md mx-auto space-y-2 text-base text-slate-700 dark:text-zinc-300 leading-relaxed">
            <p>
              Comme un patron qui nécessite un petit ajustement, notre atelier numérique a rencontré un contretemps.
            </p>
            <p className="text-sm text-slate-600 dark:text-zinc-400 italic">
              Pas de panique ! Notre équipe a été informée et travaille déjà sur la réparation. 🪡
            </p>
          </div>

          {/* Barre de progression du signalement d'erreur */}
          {ErrorProgress ? (
            <ErrorProgress error={error} errorInfo={{ componentStack }} />
          ) : null}
        </div>

        {/* Action buttons with craft theme */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
          <button
            onClick={() => window.location.reload()}
            className="group px-6 py-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
          >
            <svg className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Réessayer
          </button>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 rounded-full border-2 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/30 font-semibold transition-all duration-300 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour
          </button>
        </div>

        {/* Reassuring footer */}
        <div className="pt-6 border-t border-amber-200 dark:border-zinc-800 mt-8">
          <p className="text-xs text-slate-800 dark:text-zinc-500 flex items-center justify-center gap-2">
            <span className="inline-block animate-pulse">💡</span>
            <span>Vos créations sont en sécurité, prenez une pause thé !</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function LazySentryBoundary({ children }) {
  const [Sentry, setSentry] = useState(null);

  useEffect(() => {
    let mounted = true;
    import('@sentry/react')
      .then((mod) => {
        if (mounted) setSentry(mod);
      })
      .catch((error) => {
        console.warn('Sentry non chargé (mode dégradé)', error);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!Sentry) {
    return <>{children}</>;
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <LazySentryBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </LazySentryBoundary>
  </React.StrictMode>
);

// Enregistrement du Service Worker pour la PWA
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('✅ Service Worker enregistré:', registration.scope);

        // Vérifie les mises à jour toutes les heures
        const updateInterval = setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

        // Cleanup interval si le service worker est unregistered
        const handleUnregister = () => {
          clearInterval(updateInterval);
        };

        // Note: Il n'y a pas d'événement 'unregister' standard.
        // Le cleanup se fera automatiquement si la page est fermée.
        // Pour une gestion plus robuste, on pourrait utiliser beforeunload,
        // mais ce n'est pas critique car le navigateur nettoie les intervals
        // quand l'onglet est fermé.
        window.addEventListener('beforeunload', handleUnregister);

        // Gestion des mises à jour
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Nouvelle version disponible
              console.log('🔄 Nouvelle version disponible');
            }
          });
        });
      })
      .catch((error) => {
        console.error('❌ Échec enregistrement Service Worker:', error);
      });
  });
}
